// <insta-carousel> — 3D coverflow around the Instafeed app's posts. The app's items are moved into our stage (their click
// handlers still open the app's popup). Centre post = highlight; neighbours smaller and overlapping. transform/opacity only.
// Auto-advances every data-interval s; paused while the popup is open, tab hidden, section off-screen, keyboard focus inside,
// or the user paused the centre video. Reduced motion: no autoplay, instant moves. Also skins the popup (Follow button).
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const phone = window.matchMedia('(max-width: 1023px)');
let skinned = false;
const mod = (n, m) => ((n % m) + m) % m;

class InstaCarousel extends HTMLElement {
  connectedCallback() {
    this.sec = this.closest('.ig');
    this.view = this.querySelector('.ig-view'); this.stage = this.querySelector('.ig-stage');
    this.source = this.querySelector('.ig-source'); this.play = this.querySelector('.ig-play');
    this.items = []; this.seen = new Set(); this.a = 0; this.elapsed = 0; this.userPaused = false;
    this.max = Math.min(24, Math.max(6, +this.dataset.count || 12));
    this.interval = Math.min(5, Math.max(3, +this.dataset.interval || 4)) * 1000;
    this.style.setProperty('--ig-dur', this.interval / 1000 + 's');
    this.visible = true; this.focused = false;

    const reveal = () => this.sec.classList.add('in');
    if (reduce.matches || !('IntersectionObserver' in window)) reveal();
    else new IntersectionObserver((es, io) => es.forEach((e) => { if (e.isIntersecting) { reveal(); io.disconnect(); } }), { threshold: 0.2 }).observe(this.sec);
    if ('IntersectionObserver' in window) new IntersectionObserver((es) => { this.visible = es[0].isIntersecting; if (this.visible) this.armSettle(); this.sync(); }, { threshold: 0.2 }).observe(this.view);

    this.querySelector('.ig-prev').addEventListener('click', () => this.go(this.a - 1, true));
    this.querySelector('.ig-next').addEventListener('click', () => this.go(this.a + 1, true));
    this.play.addEventListener('click', (e) => { e.stopPropagation(); this.toggleVideo(); });
    // the app delegates clicks from document in the capture phase, so ours has to run earlier: on window
    window.addEventListener('click', (e) => { if (this.stage.contains(e.target)) this.onClick(e); }, true);
    this.addEventListener('keydown', (e) => this.onKey(e));
    this.addEventListener('focusin', (e) => { this.focused = e.target.matches(':focus-visible'); this.classList.toggle('kf', this.focused && !!e.target.closest('.is-c, .ig-play')); this.sync(); });
    this.addEventListener('focusout', () => { this.focused = false; this.classList.remove('kf'); this.sync(); });
    this.view.addEventListener('pointermove', (e) => this.classList.toggle('hov', !!e.target.closest('.is-c, .ig-play')));
    this.view.addEventListener('pointerleave', () => this.classList.remove('hov'));
    this.swipe();
    // media events do not bubble: capture them on the stage
    ['play', 'pause'].forEach((t) => this.stage.addEventListener(t, (e) => {
      // the app's own hover/touch play must not start side tiles
      if (t === 'play' && e.target !== this.items[this.a]?.querySelector('video')) e.target.pause();
      this.videoState();
    }, true));
    document.addEventListener('visibilitychange', () => this.sync());
    const mq = () => this.layout(); phone.addEventListener?.('change', mq); reduce.addEventListener?.('change', () => this.sync());

    this.mo = new MutationObserver(() => this.collect());
    this.mo.observe(this.source, { childList: true, subtree: true });
    this.collect();
    if (!('IntersectionObserver' in window)) this.armSettle();
    this.tick = setInterval(() => this.step(), 100);
    skinPopups(this.dataset.followLabel, this.dataset.followUrl, this.dataset.handle, this.dataset.tFollowOn);
  }
  disconnectedCallback() { clearInterval(this.tick); clearTimeout(this.timeout); this.mo?.disconnect(); }

  // move the app's posts (deduped by thumbnail; slider layouts add clones) into our stage
  collect() {
    const found = [...this.source.querySelectorAll('.instafeed-new-layout-item')].filter((it) => {
      const k = it.dataset.instafeedThumbnail || it.querySelector('img, video')?.currentSrc || '';
      if (!k || this.seen.has(k)) return false; this.seen.add(k); return true;
    }).slice(0, Math.max(0, this.max - this.items.length));
    if (!found.length) return;
    found.forEach((it) => { this.stage.append(it); this.items.push(it); });
    this.sec.hidden = false; this.querySelector('.ig-note').hidden = true;
    this.layout(true);
  }
  // the app only renders its posts once the section is on screen, so the 10 s grace starts at first sight
  armSettle() { if (!this.timeout) this.timeout = setTimeout(() => this.settle(), 10000); }
  settle() {
    if (this.items.length) return;
    if (this.dataset.designMode === 'true') { const n = this.querySelector('.ig-note'); n.textContent = this.dataset.emptyNote; n.hidden = false; }
    else this.sec.hidden = true;
  }

  range() { return phone.matches ? 1 : 2; }
  layout(first) {
    const n = this.items.length; if (!n) return;
    const r = this.range();
    this.items.forEach((it, i) => {
      let d = mod(i - this.a + Math.floor(n / 2), n) - Math.floor(n / 2);
      if (n % 2 === 0 && d === -n / 2) d = n / 2;
      const off = Math.abs(d) > r; if (off) d = Math.sign(d || 1) * (r + 1);
      const prev = it._d; it._d = d;
      const jump = !first && prev !== undefined && Math.abs(d - prev) > 3;
      if (jump) it.classList.add('is-jump');
      it.style.setProperty('--d', d); it.style.setProperty('--ad', Math.abs(d));
      it.classList.toggle('is-c', d === 0); it.classList.toggle('is-off', off);
      it.querySelector('.instafeed-new-layout-item-container')?.setAttribute('tabindex', d === 0 ? '0' : '-1'); it.setAttribute('aria-hidden', off ? 'true' : 'false');
      if (jump) { void it.offsetWidth; it.classList.remove('is-jump'); }
    });
    this.querySelector('.ig-count b').textContent = String(this.a + 1).padStart(2, '0');
    this.querySelector('.ig-count i').textContent = String(n).padStart(2, '0');
    const c = this.items[this.a], v = c.querySelector('video');
    this.view.setAttribute('aria-label', (this.dataset.tPostOf || 'Post %n of %t').replace('%n', this.a + 1).replace('%t', n));
    this.play.hidden = !v;
    this.restartBar(); this.media();
  }
  go(i, user) {
    if (!this.items.length) return;
    this.a = mod(i, this.items.length); if (user) this.userPaused = false;
    this.layout();
  }

  // videos: only the centre one plays (muted, looped); no hover behaviour
  // Instafeed renders a video post as a bare <img> when its "Video autoplay" is No: build the <video> from the popup's copy
  ensureVideo(it) {
    let v = it.querySelector('video'); if (v) return v;
    const box = it.querySelector('.instafeed-new-layout-item-container'), id = box?.dataset.instafeedOpenId; if (!id) return null;
    const lb = document.getElementById(id), src = (lb?.querySelector('video') || document.getElementById(`video-${id.replace(/-insta-feed$/, '')}-insta-feed`))?.currentSrc
      || lb?.querySelector('video')?.getAttribute('src');
    if (!src) return null;
    v = document.createElement('video'); v.className = 'auto-loop-videos ig-made'; v.src = src;
    v.poster = it.querySelector('img')?.currentSrc || it.dataset.instafeedThumbnail || '';
    v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('loop', ''); v.preload = 'none';
    it.insertBefore(v, box); return v;
  }
  media() {
    const n = this.items.length;
    this.items.forEach((it, i) => {
      const centre = i === this.a, next = i === mod(this.a + 1, n);
      const v = centre ? this.ensureVideo(it) : it.querySelector('video'); if (!v) return;
      // iOS only autoplays inline when muted/playsinline are set as attributes and properties before play()
      v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('loop', '');
      v.muted = true; v.defaultMuted = true; v.loop = true; v.playsInline = true;
      v.preload = centre || next ? 'metadata' : 'none';
      if (centre && !this.userPaused && !reduce.matches && this.canRun(false)) v.play()?.catch(() => this.videoState());
      else v.pause();
    });
    this.videoState();
  }
  videoState() {
    const v = this.items[this.a]?.querySelector('video');
    const paused = !!v && v.paused;
    this.classList.toggle('is-paused', paused);
    this.play.setAttribute('aria-label', paused ? (this.dataset.tPlay || 'Play video') : (this.dataset.tPause || 'Pause video'));
  }
  toggleVideo() {
    const v = this.items[this.a]?.querySelector('video'); if (!v) return;
    if (v.paused) { this.userPaused = false; v.play()?.catch(() => {}); } else { this.userPaused = true; v.pause(); }
    this.sync();
  }

  // auto-advance
  popupOpen() { return document.body.classList.contains('instafeed-body-no-scroll'); }
  canRun(needVisible = true) { return !document.hidden && !this.popupOpen() && !this.focused && (!needVisible || this.visible); }
  blocked() { return reduce.matches || this.userPaused || this.items.length < 2 || !this.canRun(); }
  sync() { this.classList.toggle('blocked', this.blocked()); this.media(); }
  restartBar() {
    this.elapsed = 0; this.classList.remove('run'); void this.offsetWidth;
    if (!reduce.matches && this.items.length > 1) this.classList.add('run');
    this.classList.toggle('blocked', this.blocked());
  }
  step() {
    const open = this.popupOpen();
    if (open !== this._open) { this._open = open; this.sync(); }
    if (this.blocked()) return;
    this.elapsed += 100;
    if (this.elapsed >= this.interval) this.go(this.a + 1);
  }

  onClick(e) {
    if (this.swiped) { e.preventDefault(); e.stopImmediatePropagation(); this.swiped = false; return; }
    const it = e.target.closest('.instafeed-new-layout-item'); if (!it) return;
    if (it !== this.items[this.a]) { e.preventDefault(); e.stopImmediatePropagation(); this.go(this.items.indexOf(it), true); }
  }
  onKey(e) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.a - 1, true); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.a + 1, true); }
  }
  // swipe is a gesture only: the stage never scrolls
  swipe() {
    let x0 = null, y0 = 0;
    this.view.addEventListener('pointerdown', (e) => { if (e.target.closest('button')) return; x0 = e.clientX; y0 = e.clientY; this.swiped = false; });
    const end = (e) => {
      if (x0 === null) return; const dx = e.clientX - x0, dy = e.clientY - y0; x0 = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { this.swiped = true; this.go(this.a + (dx < 0 ? 1 : -1), true); setTimeout(() => { this.swiped = false; }, 400); }
    };
    this.view.addEventListener('pointerup', end); this.view.addEventListener('pointercancel', () => { x0 = null; });
  }
}
if (!customElements.get('insta-carousel')) customElements.define('insta-carousel', InstaCarousel);

// ---- popup skin: Follow button right after the media, header link in the same tab ----
function skinPopups(label, url, handle, tpl) {
  const apply = () => {
    document.querySelectorAll('.instafeed-lightbox').forEach((lb) => {
      lb.querySelectorAll('a.follow').forEach((a) => { a.removeAttribute('target'); a.removeAttribute('rel'); });
      const head = lb.querySelector('.instafeed-header');
      if (!head || head.querySelector('.ig-follow')) return;
      const a = document.createElement('a');
      a.className = 'btn btn-ink ig-follow'; a.href = url || 'https://www.instagram.com/chalab_by_elaiksir';
      a.setAttribute('aria-label', (tpl || 'Follow %h on Instagram').replace('%h', handle || ''));
      a.innerHTML = '<span class="roll"><span></span></span>';
      const t = a.querySelector('.roll > span'); t.textContent = label || 'Follow'; t.dataset.t = label || 'Follow';
      head.append(a);
    });
  };
  if (skinned) return apply(); skinned = true;
  new MutationObserver(apply).observe(document.body, { childList: true, subtree: true });
  apply();
}
